import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Nav } from "../../Nav";
import { Footer } from "../../Footer";
import { Calendar, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet';
import { getApiBaseUrl } from '../../../config/api';

// Get API base URL from centralized config
const API_BASE_URL = getApiBaseUrl();

// Helper function to create slug from title
const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

export const BlogPage = () => {
  const { blogSlug } = useParams();
  const location = useLocation();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get blogId from navigation state first
        const blogId = location.state?.blogId;
        
        if (blogId) {
          // Fetch the blog by its ID for reliability and performance
          const response = await fetch(`${API_BASE_URL}/blogs/${blogId}`);
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const data = await response.json();

          if (data.success && data.data) {
            setBlog(data.data);
            
            // Generate canonical slug from fetched title and update URL if needed
            const generatedSlug = createSlug(data.data.title || '');
            if (blogSlug !== generatedSlug) {
              navigate(`/blogs/${generatedSlug}`, { replace: true, state: { blogId } });
            }
          } else {
            throw new Error('Blog not found');
          }
        } else {
          // Fallback: fetch all blogs and find by slug (for direct URL access or refresh)
          const response = await fetch(`${API_BASE_URL}/blogs`);
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const data = await response.json();

          if (data.success && data.data.blogs) {
            const matchedBlog = data.data.blogs.find(b => {
              const generatedSlug = createSlug(b.title);
              return generatedSlug === blogSlug;
            });

            if (matchedBlog) {
              // Fetch the full blog content by ID
              const fullBlogResponse = await fetch(`${API_BASE_URL}/blogs/${matchedBlog.blog_id}`);
              if (!fullBlogResponse.ok) {
                throw new Error(`HTTP error! Status: ${fullBlogResponse.status}`);
              }
              const fullBlogData = await fullBlogResponse.json();
              
              if (fullBlogData.success && fullBlogData.data) {
                setBlog(fullBlogData.data);
              } else {
                throw new Error('Blog not found');
              }
            } else {
              throw new Error('Blog not found');
            }
          } else {
            throw new Error('Blog not found');
          }
        }
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch blog:', err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [blogSlug, location.state?.blogId, navigate]);

  if (loading) {
    return (
      <div className="w-full overflow-x-hidden min-h-screen flex flex-col">
        <Nav />
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full overflow-x-hidden min-h-screen flex flex-col">
        <Nav />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center p-8 max-w-2xl">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Blog</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/blogs')}
              className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center mx-auto"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back to Blogs
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="w-full overflow-x-hidden min-h-screen flex flex-col">
        <Nav />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center p-8 max-w-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Blog Not Found</h2>
            <p className="text-gray-600 mb-6">The blog post you're looking for doesn't exist or may have been removed.</p>
            <button
              onClick={() => navigate('/blogs')}
              className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center mx-auto"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back to Blogs
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-hidden min-h-screen flex flex-col">
      <Helmet>
        <title>{blog.title} | Gomens AI Blog</title>
        <meta name="description" content={blog.content?.substring(0, 160) || blog.title} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.content?.substring(0, 160) || blog.title} />
        <meta property="og:image" content={blog.image_url} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://gomens.ai/blogs/${blogSlug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.content?.substring(0, 160) || blog.title} />
        <meta name="twitter:image" content={blog.image_url} />
        <link rel="canonical" href={`https://gomens.ai/blogs/${blogSlug}`} />
      </Helmet>
      <Nav />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative pt-10 pb-16 bg-gradient-to-b from-violet-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative z-10 py-8 md:py-12">
              <button
                onClick={() => navigate('/blogs')}
                className="cursor-pointer flex items-center text-violet-600 hover:text-violet-800 mb-8 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Blogs
              </button>
              
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Calendar className="h-4 w-4 mr-1.5 text-violet-500" />
                <span>{new Date(blog.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                {blog.title}
              </h1>
              
              <div className="w-full h-96 rounded-xl overflow-hidden mb-1">
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80";
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Blog Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="blog-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({...props}) => <h1 className="text-4xl font-bold mb-6 mt-8" {...props} />,
                  h2: ({...props}) => <h2 className="text-3xl font-bold mb-5 mt-7" {...props} />,
                  h3: ({...props}) => <h3 className="text-2xl font-bold mb-4 mt-6" {...props} />,
                  h4: ({...props}) => <h4 className="text-xl font-bold mb-3 mt-5" {...props} />,
                  h5: ({...props}) => <h5 className="text-lg font-bold mb-2 mt-4" {...props} />,
                  h6: ({...props}) => <h6 className="text-base font-bold mb-2 mt-3" {...props} />,
                  p: ({...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                  ol: ({...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                  li: ({...props}) => <li className="text-gray-700 leading-relaxed" {...props} />,
                  a: ({...props}) => <a className="text-violet-600 hover:text-violet-800 underline" {...props} />,
                  blockquote: ({...props}) => <blockquote className="border-l-4 border-violet-500 pl-4 italic my-4 text-gray-600" {...props} />,
                  code: ({inline, ...props}) => 
                    inline 
                      ? <code className="bg-gray-100 rounded px-1 py-0.5 text-sm font-mono text-violet-600" {...props} />
                      : <code className="block bg-gray-100 rounded p-4 text-sm font-mono overflow-x-auto mb-4" {...props} />,
                  pre: ({...props}) => <pre className="bg-gray-100 rounded p-4 overflow-x-auto mb-4" {...props} />,
                  table: ({...props}) => <table className="min-w-full divide-y divide-gray-200 my-4" {...props} />,
                  th: ({...props}) => <th className="px-4 py-2 bg-gray-50 text-left text-sm font-semibold text-gray-900" {...props} />,
                  td: ({...props}) => <td className="px-4 py-2 text-sm text-gray-700 border-t" {...props} />,
                  img: ({...props}) => <img className="rounded-lg my-4 max-w-full h-auto" {...props} />,
                  hr: ({...props}) => <hr className="my-8 border-gray-200" {...props} />,
                }}
              >
                {blog.content}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};